import { RawRule } from '@casl/ability';
import ability from 'ability';
import axios from 'axios';
import { ILoginResponseModel } from 'models/login';
import { toast } from 'react-toastify';
import authService from 'services/auth/auth-service';
import notificationService from 'services/notification/notification-service';
import { IHeaderMenu } from 'shared/constants/patient-menu-data';
import store from 'store';
import { AuthActionType } from 'store/actionTypes/auth-action-type';
import { IAction, IApplicationState } from 'store/state/app-state';
import { IModuleNotification, IRolePermission } from 'store/state/auth-state';
import { ConvertAbility } from 'utils/abilityHelper';

export const profilePhotoUpdate = (profilePhoto: string): IAction<string> => {
    return { type: AuthActionType.PATIENT_PROFILE_PIC_UPDATE, payload: profilePhoto };
};

export const profileUserNameUpdate = (userName: string): IAction<string> => {
    return { type: AuthActionType.PATIENT_PROFILE_USERNAME_UPDATE, payload: userName };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const login = (data: ILoginResponseModel) => {
    const permissions: Array<IRolePermission> = JSON.parse(data?.permissions);
    const moduleNotifications: Array<IModuleNotification> = JSON.parse(data?.moduleNotifications);
    const abilities: RawRule[] = ConvertAbility(permissions || []);
    ability.update(abilities);
    return {
        type: AuthActionType.PATIENT_LOGIN_REQUEST,
        payload: {
            ...data,
            rolePermissions: permissions,
            abilities: abilities,
            moduleNotifications: moduleNotifications,
        },
    };
};

export const logout = () => {
    return async function (dispatch) {
        return await authService
            .logout()
            .then(async () => {
                await dispatch({ type: AuthActionType.PATIENT_PATIENT_LOGOUT_REQUEST });
            })
            .catch((message: string) => {
                toast.error(message.toString());
            });
    };
};

export const refreshToken = () => {
    return async (dispatch, getState) => {
        const storeData: IApplicationState = getState();
        const authConfigs = storeData?.UserData?.authConfigs;

        if (!authConfigs?.refreshToken) {
            throw new Error('No refresh token available');
        }

        const requestBody = {
            patientIdEnc: storeData?.UserData?.patientIdEnc,
            refreshToken: authConfigs?.refreshToken,
            deviceId: authConfigs?.deviceId,
        };

        const response = await axios.post(`patients/Auth/RefreshToken`, requestBody);

        const newAccessToken = response?.data?.data?.jwtToken;
        if (!newAccessToken) {
            throw new Error('No new access token received');
        }

        dispatch({
            type: AuthActionType.PATIENT_UPDATE_ACCESS_TOKEN,
            payload: { accessToken: newAccessToken },
        });

        return newAccessToken;
    };
};

export const storeMenuDetails = (menuData: Array<IHeaderMenu>) => {
    const storeData: IApplicationState = store.getState();
    const moduleNotifications: Array<IModuleNotification> = storeData?.UserData?.moduleNotifications;
    const filteredMenus: Array<IHeaderMenu> = assignMenuNotifications(menuData, moduleNotifications);
    return { type: AuthActionType.PATIENT_STORE_MENU_DETAILS, payload: filteredMenus };
};

export const updateMenuNotifications = (moduleName: string) => {
    const storeData: IApplicationState = store.getState();
    const menuData: Array<IHeaderMenu> = storeData?.UserData?.menuDetails;
    return notificationService
        .getPatientMenuNotificationCount(storeData?.UserData?.patientIdEnc, storeData?.UserData?.userIdEnc, moduleName)
        .then(async (response) => {
            if (response?.data != undefined) {
                const updatedMenuData = assignMenuNotifications(menuData, response?.data?.data);
                store.dispatch({ type: AuthActionType.PATIENT_STORE_MENU_DETAILS, payload: updatedMenuData });
            }
        });
};

const assignMenuNotifications = (menuData: Array<IHeaderMenu>, notificationData: Array<IModuleNotification>) => {
    return menuData.map((item) => {
        const currentMenu = notificationData?.find((i2) => i2.moduleName === item.menu);
        const currentCount = item?.notifications;
        return { ...item, notifications: currentMenu !== undefined ? currentMenu?.notificationCount : currentCount };
    });
};

export const patientUpgradeToCarePlan = () => {
    return {
        type: AuthActionType.PATIENT_UPGRADE_TO_CARE_PLAN,
    };
};

export const patientPasswordResetSuccess = () => {
    return {
        type: AuthActionType.PATIENT_PASSWORD_RESET,
    };
};
